import { useFocusEffect, useLocalSearchParams } from 'expo-router';

import { type AddressType as GarnishAddressType } from '@sg/garnish';
import type { Address } from '@sg/graphql-schema';
import { type Order } from '@sg/graphql-schema';
import { FlattenedOrderStatuses, OrderType } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { parsePayments } from '@order/utils';

// getAddressTypeFromAddress needs to be injected or defined locally
// ReorderOrder type will be defined locally
import {
  type OrderStatusInfoQuery,
  useOrderStatusInfoQuery,
  useOrderStatusQuery,
} from '../../graphql/OrderStatus.generated';
import { useCanCancelOrder } from '../useCanCancelOrder';

export type UseOrderStatusRequestParams = {
  isLoggedIn: boolean;
  customerId: string;
};

export function useOrderStatusRequest(params: UseOrderStatusRequestParams) {
  const { isLoggedIn, customerId } = params;

  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled');

  // ─── Request ─────────────────────────────────────────────────────────

  const { orderId } = useLocalSearchParams<{ orderId: string }>();

  const [resultsOrderStatus, fetchOrderStatus] = useOrderStatusQuery({
    variables: { id: orderId },
    requestPolicy: 'cache-and-network',
    pause: true, // NOTE: Fetching is controlled by `useFocusEffect`
  });

  const [resultsOrdersStatusInfo, fetchOrderStatusInfo] = useOrderStatusInfoQuery({
    variables: { id: orderId },
    requestPolicy: 'cache-and-network',
    pause: true, // NOTE: Fetching is controlled by `useFocusEffect`
  });

  const results = isInStoreOrdersEnabled ? resultsOrdersStatusInfo : resultsOrderStatus;

  const { data, error, fetching, stale } = results;

  // ─── Validation ──────────────────────────────────────────────────────

  const digitalOrder = data?.orderStatus;
  const inStoreOrder = parseInStoreOrder(resultsOrdersStatusInfo?.data?.inStoreLoyaltyOrder);

  const isInvalidDigitalOrder = digitalOrder?.__typename === 'InvalidOrder';
  const isInvalidOrder = !inStoreOrder && isInvalidDigitalOrder;
  const response = isInvalidDigitalOrder ? undefined : digitalOrder;

  const status = inStoreOrder ? FlattenedOrderStatuses.Completed : response?.flattenedOrderStatus;

  const hasNoData = data === undefined && !error;
  const isFetching = hasNoData || fetching || stale;
  const isMissingStatus = !status;
  const isInvalidResponse = !isFetching && (isInvalidOrder || isMissingStatus);
  const isInvalidOrderStatus = Boolean(error) || isInvalidResponse;
  const isInStoreOrder = Boolean(inStoreOrder);

  // ─── Query On Focus ──────────────────────────────────────────────────

  useFocusEffect(isInStoreOrdersEnabled ? fetchOrderStatusInfo : fetchOrderStatus);

  // ─── Parsing Response ────────────────────────────────────────────────

  const order = (response?.order ?? inStoreOrder) as Partial<Order>;
  const courierDetails = response?.courierDetails ?? undefined;
  const orderStatus = status ?? FlattenedOrderStatuses.Failed;

  const restaurant = order?.restaurant;
  const restaurantId = restaurant?.id;
  const isRestaurantOutpost = restaurant?.isOutpost;

  const deliveryDetails = order?.deliveryOrderDetail;
  const deliveryAddress = deliveryDetails?.address;

  const hasSeveralDropoffLocation = (restaurant?.availableDropOffLocations?.length ?? 0) > 1;
  const dropoffLocation =
    isRestaurantOutpost && hasSeveralDropoffLocation
      ? (order?.dropoffLocation ?? undefined)
      : undefined;

  const orderType = getOrderType(order?.orderType, isRestaurantOutpost);

  const payments = parsePayments(order?.payments);

  const orderToCancel: Parameters<typeof useCanCancelOrder>[2] =
    order?.id && order?.restaurant?.id && order?.wantedTime
      ? {
          id: order.id,
          canCancel: Boolean(restaurant),
          restaurant: {
            id: order.restaurant.id,
          },
          wantedTime: order.wantedTime,
        }
      : undefined;

  const { canCancel, refetchCanCancelStatus } = useCanCancelOrder(
    isLoggedIn,
    customerId,
    orderToCancel,
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    isFetching,
    isInvalidOrderStatus,
    isInStoreOrder,

    order,
    reorderOrder: convertToReorderOrder(order),
    payments,
    courierDetails,
    orderStatus,

    hasDeliveryAddress: Boolean(deliveryAddress),
    deliveryAddressName: deliveryAddress?.name ?? undefined,
    deliveryAddressStreet: deliveryAddress?.street,
    deliveryAddressSecondaryStreet: deliveryAddress?.secondaryStreet ?? undefined,
    deliveryAddressCity: deliveryAddress?.city,
    deliveryAddressState: deliveryAddress?.state,
    deliveryAddressZipCode: deliveryAddress?.zipCode,
    deliveryAddressNotes: deliveryAddress?.notes ?? undefined,
    deliveryAddressLatitude: deliveryAddress?.latitude,
    deliveryAddressLongitude: deliveryAddress?.longitude,
    deliveryAddressType: getAddressTypeFromAddress(deliveryAddress),
    deliveryPreference: deliveryAddress?.deliveryPreference,
    deliveryEstimatedTime: deliveryDetails?.estimatedDeliveryTime ?? undefined,

    locationName: restaurant?.name,
    locationAddress: restaurant?.address,
    locationCity: restaurant?.city,
    locationState: restaurant?.state,
    locationZipCode: restaurant?.zipCode,
    locationLatitude: restaurant?.latitude,
    locationLongitude: restaurant?.longitude,
    dropoffLocation,

    orderId,
    orderType,
    orderTotal: order?.total ?? inStoreOrder?.ledger?.total ?? undefined,
    orderWantedTime: order?.wantedTime,
    orderLedger: order?.ledger ?? {},
    orderLineItems: order?.lineItems ?? [],
    vendorId: order?.vendorId,
    restaurantId,
    canCancel,
    refetchCanCancelStatus,

    isPendingFeedback: order?.isPendingFeedback,
    isTrackable: order?.canTrackOrderStatus,
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * The address type is derived from the name, either home/work or custom.
 */
const getAddressTypeFromAddress = (
  address?: Pick<Partial<Address>, 'name'> | null,
): AddressType => {
  if (address?.name === 'home') return 'home';

  if (address?.name === 'work') return 'work';

  return 'custom';
};

const convertToReorderOrder = (order?: Partial<Order>): ReorderOrder => {
  return {
    restaurant: {
      id: order?.restaurant?.id ?? '',
      name: order?.restaurant?.name ?? '',
      slug: order?.restaurant?.slug ?? '',
    },
    deliveryOrderDetail: {
      id: order?.deliveryOrderDetail?.id ?? '',
      deliveryFee: order?.deliveryOrderDetail?.deliveryFee ?? 0,
      tip: order?.deliveryOrderDetail?.tip ?? 0,
      vendor: order?.deliveryOrderDetail?.vendor ?? '',
      vendorRestaurantId: order?.deliveryOrderDetail?.vendorRestaurantId ?? '',
      address: order?.deliveryOrderDetail?.address ?? null,
    },
  };
};

const getOrderType = (orderType?: OrderType, isRestaurantOutpost?: boolean): GarnishAddressType => {
  if (isRestaurantOutpost) return 'outpost';
  if (orderType === OrderType.Delivery) return 'delivery';
  if (orderType === OrderType.InStore) return 'in-store';

  return 'pickup';
};

const parseInStoreOrder = (inStoreOrder?: InStoreLoyaltyOrder | null) => {
  if (!inStoreOrder) return undefined;

  return {
    ...inStoreOrder,
    orderType: OrderType.InStore,
    lineItems: inStoreOrder.lineItems.map(parseInStoreLineItem),
  };
};

const parseInStoreLineItem = (lineItem: InStoreLoyaltyLineItem) => {
  return {
    ...lineItem,
    perItemCost: lineItem.price * 100,
    isCustom: lineItem.isCustom,
    product: {
      name: lineItem.name,
      isCustom: lineItem.isCustomProduct,
      asset: {
        url: lineItem.asset?.url,
      },
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderOrder = {
  deliveryOrderDetail?: {
    id: string;
    deliveryFee: number;
    tip: number;
    vendor: string;
    vendorRestaurantId: string;
    address: {
      id: string;
      name: string | null;
    } | null;
  } | null;
  restaurant: {
    id: string;
    name: string;
    slug: string;
  };
};

type AddressType = 'home' | 'work' | 'custom';

type InStoreLoyaltyOrder = NonNullable<OrderStatusInfoQuery['inStoreLoyaltyOrder']>;

type InStoreLoyaltyLineItem = InStoreLoyaltyOrder['lineItems'][number];
